<template>
  <section class="leave-tb-sync-page">
    <header class="page-header">
      <div class="page-title-wrap">
        <h1>请假门禁同步任务</h1>
        <div class="metric-list">
          <span v-for="item in summaryItems" :key="item.key" class="metric-chip" :class="item.tone">
            {{ item.label }} <b>{{ summaryValue(item.key) }}</b>
          </span>
          <span class="metric-chip oldest">
            最老待处理 <b>{{ formatDate(summaryValue('oldestPendingTime')) }}</b>
          </span>
        </div>
      </div>
      <el-button class="refresh-button" size="small" icon="el-icon-refresh" :loading="loading" @click="refresh">
        刷新
      </el-button>
    </header>

    <div class="task-panel">
      <div class="panel-toolbar">
        <div class="toolbar-filters">
          <el-input
            v-model="studentName"
            clearable
            size="small"
            placeholder="学生姓名"
            class="filter-input"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-select v-model="bizType" clearable placeholder="业务类型" size="small" class="filter-select filter-select-biz" @change="handleSearch">
            <el-option label="普通请假" value="regular_leave" />
            <el-option label="普通返校" value="regular_return" />
            <el-option label="代请假明细" value="agent_detail" />
            <el-option label="删除普通请假" value="delete_regular_leave" />
            <el-option label="删除普通返校" value="delete_regular_return" />
            <el-option label="删除代请假明细" value="delete_agent_detail" />
          </el-select>
          <el-select v-model="status" clearable placeholder="全部状态" size="small" class="filter-select" @change="handleSearch">
            <el-option label="待处理" value="Pending" />
            <el-option label="处理中" value="Processing" />
            <el-option label="失败" value="Failed" />
            <el-option label="成功" value="Success" />
            <el-option label="无需下发" value="NotApplicable" />
          </el-select>
          <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
        </div>
      </div>

      <div class="table-host">
        <el-table v-loading="loading" :data="tasks" class="task-table" height="100%">
          <el-table-column label="业务类型" width="120" show-overflow-tooltip>
            <template slot-scope="scope">{{ formatBizType(scope.row.BizType) }}</template>
          </el-table-column>
          <el-table-column label="学生" width="90" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.StudentName || (isDeleteBiz(scope.row.BizType) ? '业务已删除' : '—') }}
            </template>
          </el-table-column>
          <el-table-column label="请假类型" width="90" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.LeaveTypeName || '—' }}</template>
          </el-table-column>
          <el-table-column prop="LeaveTimeText" label="请假时段" min-width="180" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.LeaveTimeText || '—' }}</template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <span class="status-pill" :class="statusClass(scope.row.Status)">
                <i class="status-dot"></i>{{ formatStatus(scope.row.Status) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="天波返回" min-width="160" show-overflow-tooltip>
            <template slot-scope="scope">
              <span :title="scope.row.TbResponseBody || scope.row.LastError || ''">
                {{ formatResultDetail(scope.row) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="天波ID" width="90">
            <template slot-scope="scope">
              {{ scope.row.TbThirdPartId > 0 ? scope.row.TbThirdPartId : '—' }}
            </template>
          </el-table-column>
          <el-table-column label="执行次数" width="80">
            <template slot-scope="scope">{{ scope.row.AttemptCount || 0 }}</template>
          </el-table-column>
          <el-table-column label="下次执行" width="155">
            <template slot-scope="scope">{{ formatNextRetry(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="完成时间" width="155">
            <template slot-scope="scope">{{ formatCompleted(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="80" fixed="right">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.Status === 'Failed' || scope.row.Status === 'Pending'"
                class="retry-button"
                type="text"
                @click="retry(scope.row)"
              >立即重试</el-button>
              <span v-else class="muted-action">—</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="panel-footer">
        <span class="result-count">共 {{ total }} 条任务</span>
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page="page"
          :page-size="pageSize"
          :page-sizes="[20, 50, 100]"
          :total="total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>
  </section>
</template>

<script>
import {
  getLeaveTbSyncSummary,
  getLeaveTbSyncTasks,
  retryLeaveTbSyncTask
} from '@/api/modules/misc'

export default {
  name: 'LeaveTbSyncTaskList',
  data () {
    return {
      loading: false,
      summary: {},
      tasks: [],
      studentName: '',
      bizType: '',
      status: '',
      page: 1,
      pageSize: 20,
      total: 0,
      summaryItems: [
        { key: 'pendingCount', label: '待处理', tone: 'pending' },
        { key: 'processingCount', label: '处理中', tone: 'processing' },
        { key: 'failedCount', label: '失败', tone: 'failed' }
      ]
    }
  },
  created () {
    this.refresh()
  },
  methods: {
    async refresh () {
      await Promise.all([this.loadSummary(), this.loadTasks()])
    },
    async loadSummary () {
      const response = await getLeaveTbSyncSummary()
      this.summary = response.data && response.data.response ? response.data.response : {}
    },
    summaryValue (key) {
      const pascalKey = key.charAt(0).toUpperCase() + key.slice(1)
      const value = this.summary[key] !== undefined ? this.summary[key] : this.summary[pascalKey]
      return value !== undefined && value !== null ? value : 0
    },
    async loadTasks () {
      this.loading = true
      try {
        const response = await getLeaveTbSyncTasks({
          page: this.page,
          intPageSize: this.pageSize,
          status: this.status,
          studentName: this.studentName,
          bizType: this.bizType
        })
        const result = response.data && response.data.response
        this.tasks = result && result.data ? result.data : []
        this.total = result && (result.dataCount || result.DataCount) ? (result.dataCount || result.DataCount) : 0
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.page = 1
      this.loadTasks()
    },
    async retry (task) {
      await retryLeaveTbSyncTask({ taskId: task.Id })
      this.$message.success('已立即重试执行')
      await this.refresh()
    },
    handlePageChange (page) {
      this.page = page
      this.loadTasks()
    },
    handleSizeChange (pageSize) {
      this.pageSize = pageSize
      this.page = 1
      this.loadTasks()
    },
    isDeleteBiz (value) {
      return String(value || '').startsWith('delete_')
    },
    isTerminalStatus (value) {
      return value === 'Success' || value === 'Failed' || value === 'NotApplicable'
    },
    formatBizType (value) {
      return {
        regular_leave: '普通请假',
        regular_return: '普通返校',
        agent_detail: '代请假明细',
        delete_regular_leave: '删除普通请假',
        delete_regular_return: '删除普通返校',
        delete_agent_detail: '删除代请假明细'
      }[value] || value || '—'
    },
    formatTbResponse (task) {
      const parts = []
      if (task.TbHttpStatusCode) parts.push(`HTTP ${task.TbHttpStatusCode}`)
      if (task.TbResponseCode !== null && task.TbResponseCode !== undefined) {
        parts.push(`码 ${task.TbResponseCode}`)
      }
      if (task.TbResponseMessage) parts.push(task.TbResponseMessage)
      if (!parts.length && task.TbResponseBody) parts.push(task.TbResponseBody)
      return parts.join(' / ') || '—'
    },
    formatResultDetail (task) {
      const responseText = this.formatTbResponse(task)
      if (responseText !== '—') return responseText
      return task.LastError || '—'
    },
    formatStatus (value) {
      return {
        Pending: '待处理',
        Processing: '处理中',
        Success: '已成功',
        Failed: '失败',
        NotApplicable: '无需下发'
      }[value] || value || '未知'
    },
    statusClass (value) {
      return {
        Pending: 'is-pending',
        Processing: 'is-processing',
        Success: 'is-success',
        Failed: 'is-failed',
        NotApplicable: 'is-muted'
      }[value] || 'is-muted'
    },
    formatNextRetry (task) {
      // 终态任务不会再执行，避免残留的下次执行时间造成误解
      if (!task || this.isTerminalStatus(task.Status)) return '—'
      return this.formatDate(task.NextRetryTime)
    },
    formatCompleted (task) {
      if (!task || !this.isTerminalStatus(task.Status)) return '—'
      return this.formatDate(task.CompletedTime)
    },
    formatDate (value) {
      if (!value || value === 0) return '—'
      const text = String(value)
      return text.replace('T', ' ').slice(0, 19)
    }
  }
}
</script>

<style scoped>
.leave-tb-sync-page {
  --ink: #182536;
  --muted: #7b8798;
  --line: #e7edf4;
  height: 100%;
  padding: 16px 24px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f6f8fb;
  color: var(--ink);
}
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex: 0 0 auto;
}
.page-title-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  min-width: 0;
}
.page-header h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 650;
  letter-spacing: -.2px;
  white-space: nowrap;
}
.metric-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.metric-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  background: #eef2f7;
  color: #5b6b7c;
  font-size: 12px;
  white-space: nowrap;
}
.metric-chip b {
  font-size: 13px;
  font-weight: 700;
  color: #243447;
}
.metric-chip.pending {
  background: #fff7e8;
  color: #9a7118;
}
.metric-chip.pending b { color: #b27b16; }
.metric-chip.processing {
  background: #eaf3fb;
  color: #3a6ea0;
}
.metric-chip.processing b { color: #2f74b5; }
.metric-chip.failed {
  background: #fdeeee;
  color: #a84a4a;
}
.metric-chip.failed b { color: #cc5252; }
.metric-chip.oldest {
  background: #f3f5f8;
  color: #6a7888;
}
.metric-chip.oldest b {
  color: #445566;
  font-weight: 600;
}
.refresh-button {
  border-color: #d8e2ed;
  color: #356a9e;
  background: #fff;
  flex: 0 0 auto;
}
.task-panel {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
  padding: 0 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(33, 57, 84, .035);
  box-sizing: border-box;
  overflow: hidden;
}
.panel-toolbar {
  display: flex;
  flex: 0 0 48px;
  align-items: center;
  justify-content: flex-start;
  border-bottom: 1px solid var(--line);
}
.toolbar-filters {
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-input {
  width: 140px;
}
.filter-select {
  width: 120px;
}
.filter-select-biz {
  width: 150px;
}
.table-host {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
.task-table {
  width: 100%;
}
.task-table:before {
  height: 0;
}
.stack-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  line-height: 1.35;
}
.stack-cell strong {
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
}
.stack-cell span,
.stack-cell em {
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stack-cell em {
  color: #9aa6b5;
}
.result-cell .status-pill {
  width: fit-content;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
}
.status-dot {
  width: 6px;
  height: 6px;
  margin-right: 7px;
  border-radius: 50%;
  background: currentColor;
}
.is-pending { color: #b27b16; }
.is-processing { color: #397abb; }
.is-success { color: #29916d; }
.is-failed { color: #cc5252; }
.is-muted { color: #8d99a8; }
.retry-button {
  color: #337ab7;
  font-size: 13px;
  cursor: pointer;
}
.muted-action {
  color: #c2cbd5;
}
.panel-footer {
  display: flex;
  flex: 0 0 48px;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--line);
  background: #fff;
}
.result-count {
  color: var(--muted);
  font-size: 12px;
}
.panel-footer .el-pagination {
  padding: 0;
}
@media (max-width: 1000px) {
  .leave-tb-sync-page { padding: 14px; }
  .page-header {
    align-items: flex-start;
  }
}
</style>
